import 'react-image-crop/dist/ReactCrop.css';
import React, { useRef, useState } from 'react';
import ReactCrop, { ReactCropProps } from 'react-image-crop';
import type { FileMeta } from '/imports/types';
import { defer, uInt8ToBase64 } from '/imports/utils';
import { strings } from '/imports/strings';
import { initiateAndroidUpload, AndroidClient } from '/imports/android';
import { Button } from '/imports/ui/Button';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { finishFileUpload, startFileUpload, uploadFileChunk } from '/imports/api/files';
import { FileInputProps } from '/imports/ui/FileInput';
import { createPortal } from 'react-dom';

const imageInputClassName = [
  '[&_.wrapper]:flex [&_.wrapper_.meta.hide]:hidden [&_.wrapper_.file-widget-thumb]:ml-4',
  '[&_.loading-fullscreen]:opacity-40',
].join(' ');

const cropperClassName = [
  'fixed top-0 left-0 z-[3] flex h-full w-full flex-col justify-around bg-overlay',
  '[&_.ReactCrop]:m-auto [&_.ReactCrop]:max-h-full [&_.ReactCrop]:max-w-full [&_.ReactCrop]:overflow-auto',
  '[&_.ReactCrop]:[scrollbar-width:0px] [&_.ReactCrop]:[-ms-overflow-style:none]',
  '[&_.ReactCrop::-webkit-scrollbar]:h-0 [&_.ReactCrop::-webkit-scrollbar]:w-0',
  '[&_.ReactCrop_.ReactCrop__image]:h-full! [&_.ReactCrop_.ReactCrop__image]:w-full!',
  '[&_.ReactCrop_.ReactCrop__child-wrapper]:h-full! [&_.ReactCrop_.ReactCrop__child-wrapper]:w-full!',
  '[&_.ReactCrop_.ReactCrop__image_img]:max-h-full! [&_.ReactCrop_.ReactCrop__image_img]:max-w-full!',
  '[&_.ReactCrop_.ReactCrop__child-wrapper_img]:max-h-full! [&_.ReactCrop_.ReactCrop__child-wrapper_img]:max-w-full!',
  '[&_.buttons]:absolute [&_.buttons]:top-4 [&_.buttons]:right-4 [&_.buttons]:z-[-1] [&_.buttons]:h-8 [&_.buttons]:justify-end [&_.buttons]:bg-transparent',
  '[&_.buttons_button]:ml-4',
].join(' ');

export interface ImageInputProps extends FileInputProps, Omit<ReactCropProps, 'onChange'> {
  className?: string;
  placeholder?: string;
  value?: FileMeta;
  disabled?: boolean;
  onChange?(fileMeta: FileMeta): void;
}

export const ImageInput = (props: ImageInputProps) => {
  const { value, id, aspect } = props;
  const [file, setFile] = useState<File>();
  const [src, setSrc] = useState<string>();
  const [crop, setCrop] = useState<any>({});
  const [image, setImage] = useState<HTMLImageElement>();
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<any>(undefined);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(e.target.files || [])[0];
    file && handleFile(file);
  };
  const handleFile = (file: File) => {
    setFile(file);
    const reader = new FileReader();
    reader.addEventListener('load', () => setSrc(reader?.result as string || undefined));
    reader.readAsDataURL(file);
  };
  const handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const image = event.currentTarget;
    setImage(image);
    const height = 0.75 * image.height;
    const width = 0.75 * image.width;
    if (image.width > image.height) {
      defer(() => setCrop({ unit: 'px', height, width: aspect ? aspect * height : width }));
    }
    else {
      defer(() => setCrop({ unit: 'px', width, height: aspect ? aspect * width : height }));
    }
  };
  const handleCropComplete = async () => {
    if (!image) return;
    if (!file) return;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setUploading(true);
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject();
        },
        'image/jpeg',
        1
      );
    });
    const _file = new File([blob], file?.name, { type: 'image/jpeg' });
    let fileMeta: FileMeta | undefined = undefined;
    const { name, type, size } = _file;
    const token = await startFileUpload.call({ name, type, size });
    const stream = _file.stream() as any;
    const reader = stream.getReader();
    let read;
    let chunk;
    while ((read = await reader.read())) {
      if (read.value) {
        chunk = uInt8ToBase64(read.value);
        await uploadFileChunk.call({ token, chunk });
      }
      if (read.done) {
        fileMeta = await finishFileUpload.call(token);
        handleCropCancel();
        break;
      }
    }
    fileMeta && props.onChange?.(fileMeta);
  };
  const handleCropCancel = () => {
    setImage(undefined);
    setFile(undefined);
    setSrc(undefined);
    setUploading(false);
  };
  const handleInputClick = (e?: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (AndroidClient) initiateAndroidUpload(handleFile);
    else if (!e) fileInput.current?.click();
  };
  return (
    <div className={`${imageInputClassName}${props.className ? ` ${props.className}` : ''}`}>
      {
        value ?
          <div className="wrapper">
            <Thumb file={value} onClick={() => handleInputClick()} />
          </div>
          :
          null
      }
      {
        src ?
          (
            createPortal(
              <div className={`cropper ${cropperClassName}`}>
                <ReactCrop crop={crop}
                  onChange={(crop) => setCrop(crop)}
                  aspect={aspect}
                >
                  <img src={src} onLoad={(e) => handleImageLoaded(e)} />
                </ReactCrop>
                <div className="buttons">
                  <Button className="secondary" onClick={() => handleCropCancel()}>{strings.Cancel()}</Button>
                  <Button onClick={() => handleCropComplete()}>{strings.Done()}</Button>
                </div>
              </div>,
              document.body
            )
          )
          :
          null
      }
      {
        uploading ?
          <LoadingFullScreen />
          :
          null
      }
      <input
        id={id}
        ref={fileInput}
        className={`box-border min-h-input w-full rounded-input border border-mid bg-page px-3 py-[0.7rem] font-sans text-base text-grey-2 placeholder:capitalize${value ? ' hide hidden' : ''}`}
        type="file"
        placeholder={props.placeholder || ''}
        multiple={true}
        accept="image/*"
        disabled={props.disabled}
        onClick={(e) => handleInputClick(e)}
        onChange={(e) => handleChange(e)}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          if (!fileInput.current) return;
          fileInput.current.files = e.dataTransfer.files;
          const file = Array.from(e.dataTransfer.files)[0];
          file && handleFile(file);
        }}
      />
    </div>
  );
};

const thumbClassName = 'file-widget-thumb cursor-pointer pr-8 text-center';

interface ThumbProps {
  file: FileMeta;
  onClick?(): void;
}

const Thumb = (props: ThumbProps) => {
  const file = props.file;
  if (file.type.startsWith('image')) {
    return (
      <div className={thumbClassName}>
        <a onClick={() => props.onClick?.()}>
          <img className="max-h-28 max-w-28" src={file.url} alt={file.name} />
        </a>
      </div>
    );
  }
  if (file.type.startsWith('video')) {
    return (
      <div className={thumbClassName}>
        <a onClick={() => props.onClick?.()}>
          <video className="max-h-28 max-w-28" src={file.url} />
        </a>
      </div>
    );
  }
  if (file.type.startsWith('application/pdf')) {
    return (
      <div className={thumbClassName}>
        <a onClick={() => props.onClick?.()}>
          <iframe className="max-h-28 max-w-28" src={file.url} />
        </a>
      </div>
    );
  }
  return (
    <div className={thumbClassName}>
      <a href={file.url}>{file.name}</a>
    </div>
  );
};